import { types } from "mobx-state-tree";
import { UIDocumentGroup } from "./ui-document-group";
/**
 * This model is used to track which document group of a tab is open, which documents are open in
 * each document group, and which secondaryDocuments (comparison) documents are open in each
 * document group.
 * For the StudentGroupView the currentDocumentGroupId is the student group id.
 * For the SortWorkView the currentDocumentGroupId in an encoding the filters for group documents
 * that are currently open.
 *
 * TODO: Create different tab types so so the models can more clearly match the UI
 */
export const UITabModel = types
    .model("UITab", {
    id: types.identifier,
    /**
     * The currently open document group on this tab. This could be a subtab, a workgroup,
     * or a collapsible section on the sort work tab.
     */
    currentDocumentGroupId: types.maybe(types.string),
    /**
     * Document groups that the user has visited.
     */
    visitedDocumentGroups: types.map(UIDocumentGroup),
})
    .views(self => ({
    /**
     * Return the document group. Note: if there is no state saved about this document
     * group then undefined will be returned. Use getOrCreateDocumentGroup if you need to save
     * state.
     * @param docGroupId
     * @returns
     */
    getDocumentGroup(docGroupId) {
        return self.visitedDocumentGroups.get(docGroupId);
    },
}))
    .views(self => ({
    /**
     * Note: If there hasn't been any state saved about the currentDocumentGroupId then
     * this will return undefined. Use getOrCreateDocumentGroup if you need to save
     * state.
     */
    get currentDocumentGroup() {
        if (!self.currentDocumentGroupId)
            return undefined;
        return self.getDocumentGroup(self.currentDocumentGroupId);
    },
    getDocumentGroupPrimaryDocument(docGroupId) {
        var _a;
        return (_a = self.visitedDocumentGroups.get(docGroupId)) === null || _a === void 0 ? void 0 : _a.primaryDocumentKey;
    },
    getDocumentGroupSecondaryDocument(docGroupId) {
        var _a;
        return (_a = self.visitedDocumentGroups.get(docGroupId)) === null || _a === void 0 ? void 0 : _a.secondaryDocumentKey;
    }
}))
    .actions(self => ({
    /**
     * This should not be used from view. Because this is an action the properties it reads
     * will not be observed by that parent view. For example if the documentGroup is
     * deleted, that would not trigger the parent view to be re-rendered.
     * @param docGroupId
     * @returns
     */
    getOrCreateDocumentGroup(docGroupId) {
        let group = self.visitedDocumentGroups.get(docGroupId);
        if (!group) {
            group = UIDocumentGroup.create({ id: docGroupId });
            self.visitedDocumentGroups.put(group);
        }
        return group;
    }
}))
    .actions(self => ({
    /**
     * This will create or update the UIDocumentGroup in this tab.
     * This will not change the currentDocumentGroup.
     *
     * @param docGroupId
     * @param documentKey
     */
    setDocumentGroupPrimaryDocument(docGroupId, documentKey) {
        const group = self.getOrCreateDocumentGroup(docGroupId);
        group.setPrimaryDocumentKey(documentKey);
    },
    setDocumentGroupSecondaryDocument(docGroupId, documentKey) {
        const group = self.getOrCreateDocumentGroup(docGroupId);
        group.setSecondaryDocumentKey(documentKey);
    }
}))
    .actions(self => ({
    openDocumentGroupPrimaryDocument(docGroupId, documentKey) {
        self.setDocumentGroupPrimaryDocument(docGroupId, documentKey);
        self.currentDocumentGroupId = docGroupId;
    },
}));
//# sourceMappingURL=data:application/json;base64,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